/**
 * nav-sync — Resolves navigation state from the current route.
 *
 * Given a NavigationData tree and the browser's current route, walks
 * tabs → categories → pages to find the matching entry and returns
 * the tab slug, titles, and breadcrumb path.
 */

import type { NavigationData, NavTab, NavCategory, NavPage } from "@cogo/types-v2"

export interface NavSyncResult {
  tabSlug: string
  tabTitle: string
  pageTitle: string
  breadcrumb: Array<{ label: string; route?: string }>
}

/**
 * Walk the navigation tree and return the best match for `currentRoute`.
 * Returns `null` when no match is found.
 */
export function resolveNavFromRoute(
  navData: NavigationData,
  currentRoute: string,
  matchStrategy: "prefix" | "exact" = "prefix",
): NavSyncResult | null {
  // Normalise trailing slash
  const route = currentRoute.replace(/\/+$/, "") || "/"

  // --- Try tabs first ---
  if (navData.tabs?.length) {
    // 1st pass: exact match
    for (const tab of navData.tabs) {
      for (const cat of tab.categories) {
        for (const page of cat.pages) {
          const pageRoute = page.route.replace(/\/+$/, "") || "/"
          if (pageRoute === route) {
            return buildResult(tab, cat, page)
          }
        }
      }
    }

    // 2nd pass: prefix match (only if strategy allows)
    if (matchStrategy === "prefix") {
      // Find the longest prefix match
      let best: { tab: NavTab; cat: NavCategory; page: NavPage; len: number } | null = null
      for (const tab of navData.tabs) {
        for (const cat of tab.categories) {
          for (const page of cat.pages) {
            const pageRoute = page.route.replace(/\/+$/, "") || "/"
            if (route.startsWith(pageRoute) && (!best || pageRoute.length > best.len)) {
              best = { tab, cat, page, len: pageRoute.length }
            }
          }
        }
      }
      if (best) {
        return buildResult(best.tab, best.cat, best.page)
      }

      // Fallback: match tab by route prefix (e.g. /admin/financials/... → financials tab)
      for (const tab of navData.tabs) {
        for (const cat of tab.categories) {
          for (const page of cat.pages) {
            const segments = page.route.split("/").filter(Boolean)
            // Check if route shares the same top segments as this tab's pages
            if (segments.length >= 2 && route.startsWith("/" + segments.slice(0, 2).join("/"))) {
              return {
                tabSlug: tab.slug,
                tabTitle: tab.title,
                pageTitle: "",
                breadcrumb: [{ label: tab.title }],
              }
            }
          }
        }
      }
    }
  }

  // --- Flat pages ---
  if (navData.pages?.length) {
    for (const page of navData.pages) {
      const pageRoute = page.route.replace(/\/+$/, "") || "/"
      if (pageRoute === route) {
        return {
          tabSlug: "",
          tabTitle: "",
          pageTitle: page.title,
          breadcrumb: [{ label: page.title, route: page.route }],
        }
      }
    }
    if (matchStrategy === "prefix") {
      let best: NavPage | null = null
      let bestLen = 0
      for (const page of navData.pages) {
        const pageRoute = page.route.replace(/\/+$/, "") || "/"
        if (route.startsWith(pageRoute) && pageRoute.length > bestLen) {
          best = page
          bestLen = pageRoute.length
        }
      }
      if (best) {
        return {
          tabSlug: "",
          tabTitle: "",
          pageTitle: best.title,
          breadcrumb: [{ label: best.title, route: best.route }],
        }
      }
    }
  }

  return null
}

function buildResult(tab: NavTab, cat: NavCategory, page: NavPage): NavSyncResult {
  return {
    tabSlug: tab.slug,
    tabTitle: tab.title,
    pageTitle: page.title,
    breadcrumb: [
      { label: tab.title },
      { label: cat.title },
      { label: page.title, route: page.route },
    ],
  }
}
